import { CommonModule } from '@angular/common';
import {
  Component,
  OnDestroy,
  OnInit,
  AfterViewInit,
  Input,
  inject,
  ElementRef,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  IonButton,
  IonCard,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonIcon,
  IonThumbnail,
  IonImg,
} from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { pin, removeCircle, fileTrayStacked } from 'ionicons/icons';
import { APP_SETTINGS_TOKEN, InfoPinService } from '@crowdctrl/shared';
import { WorkspaceContextService } from '../../../../services/workspace-context.service';
import { Subscription, combineLatest } from 'rxjs';
import { startWith } from 'rxjs/operators';

addIcons({ pin, removeCircle, fileTrayStacked });

@Component({
  selector: 'app-workspace-info',
  standalone: true,
  imports: [
    CommonModule,
    IonCard,
    IonCardHeader,
    IonCardTitle,
    IonCardSubtitle,
    IonButton,
    IonIcon,
    IonThumbnail,
    IonImg,
  ],
  templateUrl: './workspace-info.component.html',
  styleUrl: './workspace-info.component.css',
})
export class WorkspaceInfoComponent implements OnInit, AfterViewInit, OnDestroy {
  private workspaceContextService = inject(WorkspaceContextService);
  private appSettings = inject(APP_SETTINGS_TOKEN);
  private infoPinService = inject(InfoPinService);
  private elementRef = inject(ElementRef);

  /**
   * When false, no in-page or pinned shell surface is shown; clears workspace pinned template
   * from the shell (use a zero-size instance on routes that should not show workspace info).
   */
  @Input() showInfoCard = true;

  @ViewChild('infoCardTpl') infoCardTpl?: TemplateRef<unknown>;

  workspace$ = this.workspaceContextService.workspace$;
  workspaceId$ = this.workspaceContextService.workspaceId$;
  settings$ = this.appSettings.settings$;
  isPinned$ = this.infoPinService
    .pinnedState$('workspace')
    .pipe(startWith(this.infoPinService.isPinned('workspace')));

  private subscriptions = new Subscription();
  private scrollTimeout: ReturnType<typeof setTimeout> | undefined;
  /** Removes ion-content scroll listener when set */
  private scrollTeardown?: () => void;
  isScrolling = false;

  /**
   * When true, binds `.scrolling` while `ion-content` scrolls so CSS can dim the pinned card
   * (see `.info-card-wrapper--toolbar.scrolling`). Disabled for now; scroll listener still runs.
   */
  readonly pinnedInfoScrollTransparencyEffect = false;

  constructor() {
    const current = this.infoPinService.getPinnedContent();
    if (current && current.type !== 'workspace') {
      this.infoPinService.setPinnedContent(current.type, null);
    }
  }

  ngOnInit(): void {
    if (!this.showInfoCard) {
      this.clearWorkspacePinnedShellTemplate();
    }
  }

  ngAfterViewInit(): void {
    if (!this.showInfoCard) {
      return;
    }
    this.subscriptions.add(
      combineLatest([this.workspace$, this.settings$, this.isPinned$]).subscribe(
        ([workspace, settings, pinned]) => {
          if (!this.infoCardTpl) return;

          if (pinned && workspace && settings) {
            this.infoPinService.setPinnedContent('workspace', this.infoCardTpl, {
              workspace,
              settings,
            });
            this.setupScrollListener();
          } else {
            this.removeScrollListener();
            if (!pinned) {
              const cur = this.infoPinService.getPinnedContent();
              if (cur?.type === 'workspace') {
                this.infoPinService.setPinnedContent('workspace', null);
              }
            }
          }
        },
      ),
    );
  }

  private clearWorkspacePinnedShellTemplate(): void {
    const cur = this.infoPinService.getPinnedContent();
    if (cur?.type === 'workspace') {
      this.infoPinService.setPinnedContent('workspace', null);
    }
  }

  private setupScrollListener(): void {
    this.removeScrollListener();
    const ionContent = this.elementRef.nativeElement.closest('ion-content');
    if (!ionContent) return;

    void ionContent.getScrollElement().then((scrollEl: HTMLElement) => {
      const handleScroll = () => {
        this.isScrolling = true;
        if (this.scrollTimeout) clearTimeout(this.scrollTimeout);
        this.scrollTimeout = setTimeout(() => {
          this.isScrolling = false;
        }, 150);
      };
      scrollEl.addEventListener('scroll', handleScroll, { passive: true });
      this.scrollTeardown = () => scrollEl.removeEventListener('scroll', handleScroll);
    });
  }

  private removeScrollListener(): void {
    this.isScrolling = false;
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
      this.scrollTimeout = undefined;
    }
    this.scrollTeardown?.();
    this.scrollTeardown = undefined;
  }

  ngOnDestroy(): void {
    if (this.showInfoCard) {
      this.infoPinService.setPinnedContent('workspace', null);
    }
    this.removeScrollListener();
    this.subscriptions.unsubscribe();
  }

  togglePin(): void {
    this.infoPinService.togglePinned('workspace');
  }

  getBadgeColor(status?: string): string {
    if (!status) return 'medium';
    const normalized = status.toLowerCase();
    switch (normalized) {
      case 'active':
        return 'success';
      case 'inactive':
        return 'medium';
      case 'archived':
        return 'medium';
      default:
        return 'medium';
    }
  }
}
